import React from 'react';
import { Icon } from 'semantic-ui-react';
import { Doughnut } from 'react-chartjs-2';

/**
 * Reusable Segmentation Charts component
 * Displays two doughnut charts for Sandoz and Market segments
 */
export default class SegmentationCharts extends React.Component {

	isDarkMode = () => {
		if (typeof document === 'undefined') {
			return false;
		}

		const htmlTheme = document.documentElement.getAttribute('data-theme');
		const bodyTheme = document.body ? document.body.getAttribute('data-theme') : null;
		return htmlTheme === 'dark' || bodyTheme === 'dark';
	}

	/**
	 * Calculate segmentation counts from panel data
	 */
	calculateSegmentationCounts = (panelData) => {
		const sandozSegmentCounts = {};
		const marketSegmentCounts = {};
		
		panelData.forEach(t => {
			const seg = t.segmentation;
			
			// Treat empty or "NA" segmentation as "NA"
			if (!seg || seg === 'NA' || seg.trim() === '') {
				sandozSegmentCounts['NA'] = (sandozSegmentCounts['NA'] || 0) + 1;
				marketSegmentCounts['NA'] = (marketSegmentCounts['NA'] || 0) + 1;
				return;
			}
			
			// Sandoz segment (first character)
			const sandozSeg = seg.charAt(0);
			sandozSegmentCounts[sandozSeg] = (sandozSegmentCounts[sandozSeg] || 0) + 1;
			
			// Market segment (second character, if exists)
			const marketSeg = seg.length >= 2 ? seg.charAt(1) : 'NA';
			marketSegmentCounts[marketSeg] = (marketSegmentCounts[marketSeg] || 0) + 1;
		});

		return { sandozSegmentCounts, marketSegmentCounts };
	}

	/**
	 * Get color for a segment
	 */
	getSegmentColor = (segment) => {
		const segmentColors = {
			'A': '#2B662D',  // Zöld
			'B': '#2085D0',  // Kék
			'C': '#FF85DF',  // Rózsaszín
			'D': '#FFA502',  // Narancs
			'E': '#9B59B6',  // Lila
			'F': '#F1C40F',  // Sárga
			'G': '#1ABC9C',  // Türkiz
			'NA': '#C9CED6', // Semleges szürke
		};
		return segmentColors[segment] || '#95A5A6';
	}

	/**
	 * Create chart data object
	 */
	createChartData = (segmentCounts, label, isDarkMode) => {
		const segments = Object.keys(segmentCounts).sort();
		return {
			labels: segments.map(seg => `${seg} (${segmentCounts[seg]})`),
			datasets: [{
				label: label,
				data: segments.map(seg => segmentCounts[seg]),
				backgroundColor: segments.map(seg => this.getSegmentColor(seg)),
				borderColor: isDarkMode ? '#8f99a5' : 'transparent',
				hoverBorderColor: isDarkMode ? '#a5afbb' : 'transparent',
				borderWidth: isDarkMode ? 1 : 0,
				hoverBorderWidth: isDarkMode ? 1 : 0
			}]
		};
	}

	getChartOptions = (isDarkMode) => {
		if (!isDarkMode) {
			return {};
		}

		return {
			legend: {
				labels: {
					fontColor: '#dce0e5'
				}
			}
		};
	}

	render() {
		const { panelData, title } = this.props;
		const isDarkMode = this.isDarkMode();
		
		// Calculate segmentation counts
		const { sandozSegmentCounts, marketSegmentCounts } = this.calculateSegmentationCounts(panelData);
		
		// Create chart data
		const sandozChartData = this.createChartData(sandozSegmentCounts, 'Sandoz szegmens', isDarkMode);
		const marketChartData = this.createChartData(marketSegmentCounts, 'Piaci szegmens', isDarkMode);
		const chartOptions = this.getChartOptions(isDarkMode);
		
		const totalCount = panelData.length;

		return (
			<div>
				{title && <h4>{title}</h4>}
				<div style={{ marginTop: title ? '20px' : '-24px', marginBottom: '0px' }}>
					<h3><Icon name='circle notched'/> Sandoz szegmens ({totalCount})</h3>
					<Doughnut data={sandozChartData} options={chartOptions} width={40} height={40} />											
				</div>
				<div style={{ marginTop: '40px', marginBottom: '40px' }}>
					<h3><Icon name='circle notched'/> Piaci szegmens ({totalCount})</h3>
					<Doughnut data={marketChartData} options={chartOptions} width={40} height={40} />											
				</div>
			</div>
		);
	}
}
